// تقرير الحركة اليومية — بالتصميم الموحّد
import React, { useState, useEffect, useRef, useCallback } from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { Box, Paper, Typography, TextField, IconButton, Tooltip, CircularProgress } from '@mui/material';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import EventIcon from '@mui/icons-material/Event';

import axios from '../../axios';
import { ReportPage, ReportFilters, fieldSx, fmtMoney } from '../../components/Reports/ReportKit';
import { PrintSheet, PrintSummary, P, useReportPrint, useCompanyInfo, fmtDate } from '../../components/Reports/PrintKit';

const BORDER = '#e5e7eb';
const n = (v) => Number(v) || 0;

function Card({ title, icon, color, children, maxHeight }) {
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '14px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.1, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fafbfc' }}>
        <Box sx={{ width: 28, height: 28, borderRadius: '8px', display: 'grid', placeItems: 'center', bgcolor: `${color}1a`, color, '& svg': { fontSize: 17 } }}>
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 14.5 }}>{title}</Typography>
      </Box>
      <Box sx={{ overflow: 'auto', maxHeight }}>{children}</Box>
    </Paper>
  );
}

// تفصيل نقدي / بنك / آجل مع شريط النسبة
function Breakdown({ items, total, totalLabel }) {
  const base = Math.abs(total) || 1;
  return (
    <Box sx={{ p: 1.5, display: 'grid', gap: 1.25 }}>
      {items.map(([label, value, color]) => (
        <Box key={label}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: .4 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{label}</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color, direction: 'ltr' }}>{fmtMoney(value)}</Typography>
          </Box>
          <Box sx={{ height: 6, borderRadius: 3, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
            <Box sx={{ width: `${Math.min(100, (Math.abs(n(value)) / base) * 100)}%`, height: '100%', bgcolor: color }} />
          </Box>
        </Box>
      ))}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: `1px dashed ${BORDER}` }}>
        <Typography sx={{ fontWeight: 800 }}>{totalLabel}</Typography>
        <Typography sx={{ fontWeight: 900, fontSize: 16, direction: 'ltr' }}>{fmtMoney(total)}</Typography>
      </Box>
    </Box>
  );
}

// جدول أرصدة: افتتاحي / حركة / ختامي
function BalancesTable({ rows = [], emptyText, creditNormal = false }) {
  if (!rows.length) {
    return <Typography sx={{ py: 3, textAlign: 'center', color: '#94a3b8', fontSize: 13.5 }}>{emptyText}</Typography>;
  }
  return (
    <Box component="table" sx={{
      width: '100%', borderCollapse: 'collapse', fontSize: 13,
      '& th': { position: 'sticky', top: 0, bgcolor: '#fff', fontWeight: 800, color: '#475569', textAlign: 'right', px: 1.25, py: .8, borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap' },
      '& td': { px: 1.25, py: .75, borderBottom: '1px solid #f1f5f9' },
      '& .num': { direction: 'ltr', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
      '& tbody tr:hover td': { bgcolor: '#f8faff' },
    }}>
      <thead><tr><th>الحساب</th><th>أول اليوم</th><th>الحركة</th><th>آخر اليوم</th></tr></thead>
      <tbody>
        {rows.map((r, i) => {
          const mv = n(r.debit) - n(r.credit);
          const good = creditNormal ? mv <= 0 : mv >= 0;
          return (
            <tr key={r.accountId ?? i}>
              <td style={{ fontWeight: 600 }}>{r.accountName}</td>
              <td className="num">{fmtMoney(r.openingBalance)}</td>
              <td className="num" style={{ fontWeight: 700, color: mv === 0 ? '#94a3b8' : good ? '#047857' : '#be123c' }}>
                {mv > 0 ? '+' : ''}{fmtMoney(mv)}
              </td>
              <td className="num" style={{ fontWeight: 800 }}>{fmtMoney(r.closingBalance)}</td>
            </tr>
          );
        })}
      </tbody>
    </Box>
  );
}

function PrintBalances({ title, rows = [] }) {
  return (
    <>
      <div style={P.section}>{title}</div>
      <table style={P.table}>
        <thead><tr>
          <th style={P.th}>الحساب</th><th style={{ ...P.th, width: 95 }}>أول اليوم</th>
          <th style={{ ...P.th, width: 95 }}>مدين</th><th style={{ ...P.th, width: 95 }}>دائن</th><th style={{ ...P.th, width: 95 }}>آخر اليوم</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.accountId ?? i}>
              <td style={P.td}>{r.accountName}</td>
              <td style={P.num}>{fmtMoney(r.openingBalance)}</td>
              <td style={P.num}>{n(r.debit) ? fmtMoney(r.debit) : ''}</td>
              <td style={P.num}>{n(r.credit) ? fmtMoney(r.credit) : ''}</td>
              <td style={{ ...P.num, fontWeight: 800 }}>{fmtMoney(r.closingBalance)}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={5} style={{ ...P.td, textAlign: 'center', color: P.colors.muted }}>لا توجد حركات</td></tr>}
        </tbody>
      </table>
    </>
  );
}

export default function DailyMovementReport() {
  const company = useCompanyInfo();
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    try {
      const res = await axios.get('/AccountingReports/daily-movement', { params: { date } });
      setData(res.data);
    } catch (err) {
      console.error('Error fetching daily movement report:', err);
      toast.error('فشل في تحميل التقرير');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    const id = setTimeout(fetchReport, 250);
    return () => clearTimeout(id);
  }, [fetchReport]);

  const shiftDay = (d) => setDate(dayjs(date).add(d, 'day').format('YYYY-MM-DD'));
  const isToday = date === dayjs().format('YYYY-MM-DD');

  const printRef = useRef(null);
  const handlePrint = useReportPrint(printRef, `الحركة-اليومية-${date}`);

  const net = n(data?.netMovement);
  const dayName = dayjs(date).toDate().toLocaleDateString('ar-SA-u-ca-gregory', { weekday: 'long' });

  return (
    <ReportPage
      title="تقرير الحركة اليومية"
      icon={<TodayOutlinedIcon />}
      badges={[{ label: `${dayName} ${fmtDate(date)}`, icon: <EventIcon /> }]}
      actions={[
        { label: 'تحديث', icon: <RefreshIcon />, onClick: fetchReport, disabled: loading },
        { label: 'طباعة', icon: <PrintOutlinedIcon />, onClick: handlePrint, disabled: !data, primary: true },
      ]}
      stats={data ? [
        { label: 'المبيعات', value: fmtMoney(data.totalSales), icon: <ShoppingCartOutlinedIcon />, color: 'primary' },
        { label: 'المشتريات', value: fmtMoney(data.totalPurchases), icon: <ShoppingBagOutlinedIcon />, color: 'warning' },
        { label: 'المقبوضات', value: fmtMoney(data.totalIncomeVouchers), icon: <TrendingUpIcon />, color: 'success' },
        { label: 'المصروفات', value: fmtMoney(data.totalExpenseVouchers), icon: <TrendingDownIcon />, color: 'error' },
        { label: 'صافي الحركة', value: fmtMoney(net), icon: <SwapVertIcon />, color: net >= 0 ? 'success' : 'error', highlight: true },
      ] : []}
      filters={
        <ReportFilters onApply={fetchReport} onReset={() => setDate(dayjs().format('YYYY-MM-DD'))} loading={loading}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
            <Tooltip title="اليوم السابق">
              <IconButton onClick={() => shiftDay(-1)} sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', width: 40, height: 40 }}>
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>
            <TextField
              size="small" fullWidth type="date" label="التاريخ" value={date}
              onChange={e => e.target.value && setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ ...fieldSx, minWidth: 150, '& input': { direction: 'ltr', textAlign: 'right' } }}
            />
            <Tooltip title="اليوم التالي">
              <span>
                <IconButton onClick={() => shiftDay(1)} disabled={isToday} sx={{ border: `1px solid ${BORDER}`, borderRadius: '10px', width: 40, height: 40 }}>
                  <ChevronLeftIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </ReportFilters>
      }
    >
      {data && (
        <Box sx={{ display: 'none' }}>
          <PrintSheet ref={printRef} title="تقرير الحركة اليومية" period={`${dayName} ${fmtDate(date)}`} company={company}>
            <PrintSummary items={[
              ['المبيعات', fmtMoney(data.totalSales)], ['المشتريات', fmtMoney(data.totalPurchases)],
              ['المقبوضات', fmtMoney(data.totalIncomeVouchers)], ['المصروفات', fmtMoney(data.totalExpenseVouchers)],
              ['صافي الحركة', fmtMoney(net)],
            ]} />
            <table style={P.table}>
              <thead><tr>
                <th style={P.th}>البيان</th><th style={P.th}>نقدي</th><th style={P.th}>بنك / شبكة</th><th style={P.th}>آجل</th><th style={P.th}>الإجمالي</th>
              </tr></thead>
              <tbody>
                <tr>
                  <td style={{ ...P.td, fontWeight: 800 }}>المبيعات</td>
                  <td style={P.num}>{fmtMoney(data.totalCashSales)}</td><td style={P.num}>{fmtMoney(data.totalBankSales)}</td>
                  <td style={P.num}>{fmtMoney(data.totalCreditSales)}</td><td style={{ ...P.num, fontWeight: 800 }}>{fmtMoney(data.totalSales)}</td>
                </tr>
                <tr>
                  <td style={{ ...P.td, fontWeight: 800 }}>المشتريات</td>
                  <td style={P.num}>{fmtMoney(data.totalCashPurchases)}</td><td style={P.num}>{fmtMoney(data.totalBankPurchases)}</td>
                  <td style={P.num}>{fmtMoney(data.totalCreditPurchases)}</td><td style={{ ...P.num, fontWeight: 800 }}>{fmtMoney(data.totalPurchases)}</td>
                </tr>
              </tbody>
            </table>
            <PrintBalances title="أرصدة الصناديق والبنوك" rows={data.cashAndBankBalances} />
            <PrintBalances title="أرصدة العملاء" rows={data.customerBalances} />
            <PrintBalances title="أرصدة الموردين" rows={data.supplierBalances} />
          </PrintSheet>
        </Box>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 260 }}><CircularProgress /></Box>
      ) : data && (
        <Box sx={{
          display: 'grid', gap: 1.5, opacity: loading ? .6 : 1, transition: 'opacity .2s',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0,1fr))', xl: 'repeat(3, minmax(0,1fr))' },
          alignItems: 'start',
        }}>
          <Card title="تحليل المبيعات" icon={<ShoppingCartOutlinedIcon />} color="#4f46e5">
            <Breakdown
              total={n(data.totalSales)} totalLabel="إجمالي المبيعات"
              items={[['نقدي', data.totalCashSales, '#059669'], ['بنك / شبكة', data.totalBankSales, '#0284c7'], ['آجل', data.totalCreditSales, '#d97706']]}
            />
          </Card>
          <Card title="تحليل المشتريات" icon={<ShoppingBagOutlinedIcon />} color="#d97706">
            <Breakdown
              total={n(data.totalPurchases)} totalLabel="إجمالي المشتريات"
              items={[['نقدي', data.totalCashPurchases, '#e11d48'], ['بنك / شبكة', data.totalBankPurchases, '#0284c7'], ['آجل', data.totalCreditPurchases, '#d97706']]}
            />
          </Card>
          <Card title="أرصدة الصناديق والبنوك" icon={<AccountBalanceWalletOutlinedIcon />} color="#059669" maxHeight={320}>
            <BalancesTable rows={data.cashAndBankBalances || []} emptyText="لا توجد حركات نقدية لهذا اليوم" />
          </Card>
          <Card title="أرصدة العملاء" icon={<GroupOutlinedIcon />} color="#0284c7" maxHeight={320}>
            <BalancesTable rows={data.customerBalances || []} emptyText="لا توجد حركات للعملاء" />
          </Card>
          <Card title="أرصدة الموردين" icon={<LocalShippingOutlinedIcon />} color="#e11d48" maxHeight={320}>
            <BalancesTable rows={data.supplierBalances || []} emptyText="لا توجد حركات للموردين" creditNormal />
          </Card>
        </Box>
      )}
    </ReportPage>
  );
}
